export type ArtifactMetadata = Record<string, unknown>;

export type ArtifactKeyRef = {
  key: string;
};

export enum ArtifactState {
  Writing = "writing",
  Finalized = "finalized",
}

/**
 * Durable reference to an artifact that is ready for downstream use.
 *
 * Implementations may include additional ref fields, but this base shape is
 * enough for shared diagnostics and manager-level behavior.
 */
export type ArtifactRef = ArtifactKeyRef & {
  artifactId: string;
  mimeType: string;
  sizeBytes: number;
  metadata?: ArtifactMetadata;
};

/**
 * Point-in-time view of an artifact, including artifacts that are still writing.
 *
 * Use snapshots for recovery, resumability, and state checks. Use refs when a
 * caller needs a finalized/uploadable artifact.
 */
export type ArtifactSnapshot<TRef extends ArtifactRef = ArtifactRef> = TRef & {
  state: ArtifactState;
};

export type ArtifactBeginArgs = ArtifactKeyRef & {
  mimeType: string;
  metadata?: ArtifactMetadata;
};

export type ArtifactAppendArgs<TChunk> = ArtifactKeyRef & {
  chunk: TChunk;
};

export type ArtifactRemoveArgs = ArtifactKeyRef & {
  removeReason?: string;
};

/**
 * Storage-specific implementation behind ArtifactManager.
 *
 * Callers serialize `append()` calls per key. `finalize()` is the durability
 * boundary: it should only resolve once the backend considers the returned ref
 * safe to treat as finalized. Lookup methods should return `null` for absent or
 * unavailable artifacts instead of throwing for normal recovery misses.
 */
export interface ArtifactBackend<TChunk, TRef extends ArtifactRef = ArtifactRef> {
  /**
   * Creates or resets durable storage for the key.
   */
  begin(args: ArtifactBeginArgs): Promise<void>;

  /**
   * Appends one chunk to an artifact that is still writing.
   */
  append(args: ArtifactAppendArgs<TChunk>): Promise<void>;

  /**
   * Marks an artifact finalized and returns its durable ref.
   */
  finalize(args: ArtifactKeyRef): Promise<TRef>;

  /**
   * Returns the current artifact state for recovery/read paths.
   */
  getSnapshot(args: ArtifactKeyRef): Promise<ArtifactSnapshot<TRef> | null>;

  /**
   * Returns a finalized artifact ref, or null if no finalized ref is available.
   */
  getRef(args: ArtifactKeyRef): Promise<TRef | null>;

  /**
   * Makes future refs/snapshots unavailable and performs backend-specific cleanup.
   */
  remove(args: ArtifactRemoveArgs): Promise<void>;
}

export enum ArtifactDiagnosticEventType {
  BeginStarted = "begin_started",
  BeginSucceeded = "begin_succeeded",
  BeginFailed = "begin_failed",
  AppendFailed = "append_failed",
  FinalizeStarted = "finalize_started",
  FinalizeSucceeded = "finalize_succeeded",
  FinalizeFailed = "finalize_failed",
  GetSnapshotResolved = "get_snapshot_resolved",
  GetSnapshotFailed = "get_snapshot_failed",
  GetRefResolved = "get_ref_resolved",
  GetRefFailed = "get_ref_failed",
  RemoveStarted = "remove_started",
  RemoveSucceeded = "remove_succeeded",
  RemoveFailed = "remove_failed",
}

export type ArtifactDiagnosticRefSummary = Pick<
  ArtifactRef,
  "artifactId" | "key" | "mimeType" | "sizeBytes"
>;

export type ArtifactDiagnosticSnapshotSummary = ArtifactDiagnosticRefSummary & {
  state: ArtifactState;
};

export type ArtifactDiagnosticEvent =
  | {
      type: ArtifactDiagnosticEventType.BeginStarted;
      key: string;
      mimeType: string;
    }
  | {
      type: ArtifactDiagnosticEventType.BeginSucceeded;
      key: string;
      mimeType: string;
    }
  | {
      type: ArtifactDiagnosticEventType.BeginFailed;
      key: string;
      mimeType: string;
      errorMessage: string;
    }
  | {
      type: ArtifactDiagnosticEventType.AppendFailed;
      key: string;
      errorMessage: string;
    }
  | {
      type: ArtifactDiagnosticEventType.FinalizeStarted;
      key: string;
    }
  | {
      type: ArtifactDiagnosticEventType.FinalizeSucceeded;
      key: string;
      artifact: ArtifactDiagnosticRefSummary;
    }
  | {
      type: ArtifactDiagnosticEventType.FinalizeFailed;
      key: string;
      errorMessage: string;
    }
  | {
      type: ArtifactDiagnosticEventType.GetSnapshotResolved;
      key: string;
      hasSnapshot: boolean;
      snapshot?: ArtifactDiagnosticSnapshotSummary;
    }
  | {
      type: ArtifactDiagnosticEventType.GetSnapshotFailed;
      key: string;
      errorMessage: string;
    }
  | {
      type: ArtifactDiagnosticEventType.GetRefResolved;
      key: string;
      hasRef: boolean;
      ref?: ArtifactDiagnosticRefSummary;
    }
  | {
      type: ArtifactDiagnosticEventType.GetRefFailed;
      key: string;
      errorMessage: string;
    }
  | {
      type: ArtifactDiagnosticEventType.RemoveStarted;
      key: string;
      removeReason?: string;
    }
  | {
      type: ArtifactDiagnosticEventType.RemoveSucceeded;
      key: string;
      removeReason?: string;
    }
  | {
      type: ArtifactDiagnosticEventType.RemoveFailed;
      key: string;
      removeReason?: string;
      errorMessage: string;
    };

export type ArtifactDiagnosticEventArgs = {
  event: ArtifactDiagnosticEvent;
};

export interface ArtifactDiagnostics {
  onEvent?: (args: ArtifactDiagnosticEventArgs) => void;
}

export type ArtifactManagerConfig<TChunk, TRef extends ArtifactRef = ArtifactRef> = {
  backend: ArtifactBackend<TChunk, TRef>;
  diagnostics?: ArtifactDiagnostics;
};
